import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  signInAnonymously,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  getDatabase,
  ref,
  set,
  onValue,
  remove,
  update,
} from "firebase/database";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  increment,
  onSnapshot,
  collection,
  query,
  where,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyBgguk5BCvlUoN4jFdBn71XdaGQReb-_d4",
  authDomain: "hopecityprayerapp.firebaseapp.com",
  projectId: "hopecityprayerapp",
  storageBucket: "hopecityprayerapp.appspot.com",
  messagingSenderId: "319564361857",
  appId: "1:319564361857:web:f2d735f0098bb5322f9d80",
  databaseURL:
    "https://hopecityprayerapp-default-rtdb.europe-west1.firebasedatabase.app",
};

const firebaseApp = initializeApp(firebaseConfig);
export const database = getDatabase(firebaseApp);
export const firestoreDatabase = getFirestore(firebaseApp);

// AUTHENTICATION

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: "select_account",
});

export const auth = getAuth();

export const createAuthUserWithEmailAndPassword = async (email, password) => {
  if (!email || !password) return;
  return await createUserWithEmailAndPassword(auth, email, password);
};

export const updateUserProfileWithDisplayName = async (displayName) => {
  await updateProfile(auth.currentUser, {
    displayName: displayName,
  });
  return auth.currentUser;
};

export const signInAuthUserWithEmailAndPassword = async (email, password) => {
  if (!email || !password) return;
  return await signInWithEmailAndPassword(auth, email, password);
};

export const signInUserAnonymously = async () => {
  return await signInAnonymously(auth);
};

export const signOutUser = async () => await signOut(auth);

export const resetPassword = async (auth, email) => {
  await sendPasswordResetEmail(auth, email);
};

// FIRESTORE DATABASE

export const createUserDocument = async (userAuth) => {
  const userDocRef = doc(firestoreDatabase, "users", userAuth.uid);
  const userSnapshot = await getDoc(userDocRef); // if userSnapshot.exists() evaluates to false, that specific doc doesn't exit

  // if user doesn't exist, set to db
  if (!userSnapshot.exists()) {
    const { displayName, email, uid } = userAuth;
    const createdAtTimestamp = new Date().getTime();

    try {
      await setDoc(userDocRef, {
        displayName,
        email,
        userUid: uid,
        createdAtTimestamp,
        role: "user",
      });
    } catch (error) {
      console.log("error creating user: ", error.message);
    }
  }

  // if user exists, return userDocRef
  return userSnapshot.data();
};

export const getUserDocument = async (userAuth) => {
  const userDocRef = doc(firestoreDatabase, "users", userAuth.uid);
  const userSnapshot = await getDoc(userDocRef);

  if (userSnapshot.exists()) {
    return userSnapshot.data();
  } else {
    console.log("No such document!");
  }
};

export const createPrayerInFirestoreDb = async (
  prayerId,
  message,
  displayName,
  userUid,
  email,
  lastPrayedTimestamp,
  receiveEmailUpdates
) => {
  const nowTimestamp = new Date().getTime();
  const prayerDocRef = doc(firestoreDatabase, "prayers", prayerId);
  const prayerSnapshot = await getDoc(prayerDocRef);

  // if prayer doesn't exist, set to db
  if (!prayerSnapshot.exists()) {
    try {
      await setDoc(prayerDocRef, {
        prayerId,
        message,
        displayName,
        userUid,
        email,
        createdAtTimestamp: nowTimestamp,
        lastPrayedTimestamp: lastPrayedTimestamp,
        prayerCount: 0,
        receiveEmailUpdates,
      });
    } catch (error) {
      console.log("error creating prayer: ", error.message);
    }
  }

  // if prayer already exists, return prayerDocRef data
  return prayerSnapshot.data();
};

export const createMailInFirestoreDb = async (
  email,
  displayName,
  prayerMessage,
  isSupportRequest
) => {
  const nowTimestamp = new Date().getTime().toString();
  const mailDocRef = doc(firestoreDatabase, "mail", nowTimestamp);
  const mailSnapshot = await getDoc(mailDocRef);

  const emailName = displayName ? displayName : "";
  const supportName = displayName ? displayName : "Someone";

  const prayerTextMessage = `
  Hello ${emailName}

  Someone just prayed for your prayer request:

  ${prayerMessage}`;

  const supporTextMessage = `
  Hello

  ${supportName} would like to connect with someone about their prayer:

  ${prayerMessage}

  Their email address is: ${email}.`;

  const prayerHtmlMessage = `
    <html>
      <head>
        <style>
        #prayer {
          padding: 14px;
          font-style: italic;
        }
        </style>
      </head>
      <body style="font-size: 18px;">
        <p >Hello ${emailName}</p>
        <p >Someone just prayed for your prayer request:</p>
          <p id="prayer">${prayerMessage}</p>
        <p style="margin-top: 14px;">Blessings</p>
        <p >Hope City Prayer</p>
      </body>
    </html>    
  `;

  const supportHtmlMessage = `
  <html>
    <head>
      <style>
      #prayer {
        padding: 14px;
        font-style: italic;
      }
      </style>
    </head>
    <body style="font-size: 18px;">
      <p >Hello</p>
      <p >${supportName} would like to connect with someone about their prayer: </p>
        <p id="prayer">${prayerMessage}</p>
      <p>Their email address is: ${email}.</p>
      <p style="margin-top: 14px;">Thanks!</p>
      <p >Hope City Prayer</p>
    </body>
  </html>    
`;

  const body = {
    to: isSupportRequest ? "contact-me@hopecityedinburgh.org" : email,
    message: {
      subject: isSupportRequest
        ? "Request for pastoral support"
        : "Someone just prayed for you",
      text: isSupportRequest ? supporTextMessage : prayerTextMessage,
      html: isSupportRequest ? supportHtmlMessage : prayerHtmlMessage,
    },
  };

  // if mail doesn't exist, set to db
  if (!mailSnapshot.exists()) {
    try {
      await setDoc(mailDocRef, body);
    } catch (error) {
      console.log("error creating mail entry: ", error.message);
    }
  }
};

export const updatePrayerInFirestoreDb = async (
  prayerId,
  lastPrayedTimestamp
) => {
  const prayerDocRef = doc(firestoreDatabase, "prayers", prayerId);
  await updateDoc(prayerDocRef, {
    lastPrayedTimestamp,
    prayerCount: increment(1),
  });
};

export const deletePrayerFromFirestoreDb = async (prayerId) => {
  const prayerDocRef = doc(firestoreDatabase, "prayers", prayerId);
  await deleteDoc(prayerDocRef);
};

export const fetchPrayersFromFirestoreDb = async () => {
  const allPrayersQuery = await query(
    collection(firestoreDatabase, "prayers"),
    where("prayerId", "!=", "null")
  );
  await onSnapshot(allPrayersQuery, (querySnapshot) => {
    const allPrayers = [];
    querySnapshot.forEach((doc) => {
      allPrayers.push(doc.data().prayerId);
    });
    return allPrayers;
  });
};

// REALTIME DATABASE

export const fetchPrayersFromDb = () => {
  const prayersRef = ref(database, "prayers");
  let array = [];
  onValue(prayersRef, (snapshot) => {
    const data = snapshot.val();
    array = data;
  });
  return Object.values(array);
};

export const writeNewPrayerToDb = (
  prayerId,
  message,
  displayName,
  userUid,
  lastPrayedTimestamp
) => {
  const nowTimestamp = new Date().getTime();
  set(ref(database, "prayers/prayer-" + nowTimestamp), {
    prayerId,
    message,
    displayName,
    userUid,
    createdAtTimestamp: nowTimestamp,
    lastPrayedTimestamp: lastPrayedTimestamp,
  });
};

export const updatePrayerInDb = (createdAtTimestamp, lastPrayedTimestamp) => {
  update(ref(database, "prayers/prayer-" + createdAtTimestamp), {
    lastPrayedTimestamp,
  });
};

export const deletePrayerFromDb = (createdAtTimestamp) => {
  remove(ref(database, "prayers/prayer-" + createdAtTimestamp));
};

export const fetchNotificationsFromDb = async () => {
  const notificationsRef = ref(database, "notifications");
  return await onValue(notificationsRef, (snapshot) => {
    const data = snapshot.val();
    // return Object.values(data);
  });
};

export const writeNewNotificationToDb = (notificationId, prayerId, userUid) => {
  const nowTimestamp = new Date().getTime();
  set(ref(database, "notifications/" + nowTimestamp), {
    notificationId,
    prayerId,
    userUid,
    createdAtTimestamp: nowTimestamp,
    read: false,
  });
};

export const updateNotificationReadStatus = (createdAtTimestamp) => {
  update(ref(database, "notifications/" + createdAtTimestamp), {
    read: true,
  });
};
